import Phaser from 'phaser';
import { LABEL_COLORS, LABEL_SIZES, type MapLabel } from '@campus/core';
import { FLOOR_DEPTHS } from './FloorRenderer.js';

const TILE = 32;

/**
 * Rendu des étiquettes de texte (`map_labels`), partagé ZoneScene /
 * EditorScene. Centrées sur leur case, dessinées au sol (voir FLOOR_DEPTHS) :
 * un avatar ou un meuble passe par-dessus, comme les noms de salles dans
 * Gather.
 */
export class LabelRenderer {
  private readonly texts = new Map<string, Phaser.GameObjects.Text>();
  private readonly tiles = new Map<string, { tileX: number; tileY: number }>();

  constructor(private readonly scene: Phaser.Scene) {}

  render(labels: MapLabel[]): void {
    this.clear();
    for (const l of labels) {
      const size = LABEL_SIZES[l.size] ?? LABEL_SIZES.medium;
      const color = LABEL_COLORS[l.color] ?? LABEL_COLORS.white;
      const text = this.scene.add.text((l.tileX + 0.5) * TILE, (l.tileY + 0.5) * TILE, l.text, {
        fontFamily: 'Arial, Helvetica, sans-serif',
        fontStyle: 'bold',
        fontSize: `${size.fontSize}px`,
        color: color.fill,
        stroke: color.stroke,
        strokeThickness: Math.max(3, Math.round(size.fontSize / 5)),
      });
      text.setOrigin(0.5, 0.5);
      // Texture rendue en 2x : reste nette quand on zoome la caméra.
      text.setResolution(2);
      text.setDepth(FLOOR_DEPTHS.labels);
      this.texts.set(l.id, text);
      this.tiles.set(l.id, { tileX: l.tileX, tileY: l.tileY });
    }
  }

  /** Étiquette sous un point du monde (éditeur : clic/glisser), la plus récente d'abord. */
  labelAt(worldX: number, worldY: number): string | null {
    const ids = [...this.texts.keys()].reverse();
    for (const id of ids) {
      if (this.texts.get(id)!.getBounds().contains(worldX, worldY)) return id;
    }
    return null;
  }

  tileOf(id: string): { tileX: number; tileY: number } | undefined {
    return this.tiles.get(id);
  }

  /** Déplacement visuel pendant un glisser (l'enregistrement se fait au relâcher). */
  moveTo(id: string, tileX: number, tileY: number): void {
    this.texts.get(id)?.setPosition((tileX + 0.5) * TILE, (tileY + 0.5) * TILE);
  }

  setAlpha(id: string, alpha: number): void {
    this.texts.get(id)?.setAlpha(alpha);
  }

  clear(): void {
    for (const t of this.texts.values()) t.destroy();
    this.texts.clear();
    this.tiles.clear();
  }
}
